import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useCreateOrderMarketMutation,
  useCreateOrderMutation,
  useModifyPositionsMutation,
} from "../../../redux/exchangeUniApi";
import toast from "react-hot-toast";
import QuantityInput from "./QuntityInput";
import { formatDecimal } from "../../../util/decimalFormatter";

const isValidNumericInput = (val, min = 0.000000001) => {
  if (!val) return { valid: false, message: "This field is required." };
  if (val < 0) return { valid: false, message: "Negative values not allowed." };
  const str = val.toString().trim();
  if (/[^0-9.]/.test(str)) {
    return {
      valid: false,
      message: "Only numeric values allowed",
    };
  }
  if (/[^0-9.]/.test(val))
    return { valid: false, message: "Enter a valid amount." };
  if ((val.match(/\./g) || []).length > 1)
    return { valid: false, message: "Only one decimal point allowed." };
  if (parseFloat(val) < min)
    return { valid: false, message: `Value must be greater then zero.` };
  return { valid: true, message: "" };
};

const TradeBox = ({
  isOrderBoxShow,
  setIsOrderBoxShow,
  existingOrder = null,
  getExistingOrder,
  leverage,
  stepSize,
}) => {
  const isEditing = !!existingOrder?.id;
  const userData = useSelector((state) => state.user.userData);
  const watchlist = useSelector((state) => state.watchlist.items);
  const priceInitializedRef = useRef(false);
  const [priceError, setPriceError] = useState("");
  const [slError, setSlError] = useState("");
  const [tpError, setTpError] = useState("");
  const [side, setSide] = useState("BUY");
  const [orderType, setOrderType] = useState("market");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [sl, setSL] = useState("");
  const [tp, setTP] = useState("");
  const [enableSL, setEnableSL] = useState(false);
  const [enableTP, setEnableTP] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [submitLock, setSubmitLock] = useState(false);
  const [total, setTotal] = useState(0);
  const [net, setNet] = useState(0);
  const [createMarketOrder] = useCreateOrderMarketMutation();
  const [createOrder] = useCreateOrderMutation();
  const [modifyOrder] = useModifyPositionsMutation();
  const selectedSymbol = useSelector((state) => state.watchlist.selectedSymbol);
  const selectedSegment = useSelector(
    (state) => state.watchlist.selectedSegment
  );
  const buyCommission = useSelector((state) => state.watchlist.buyCommission);
  const sellCommission = useSelector((state) => state.watchlist.sellCommission);
  const commissionType = useSelector((state) => state.watchlist.commissionType);

  const resetState = () => {
    setSide("BUY");
    setOrderType("market");
    setQuantity("");
    setPrice("");
    setSL("");
    setTP("");
    setEnableSL(false);
    setEnableTP(false);
    setTotal(0);
    setNet(0);
    setPriceError("");
    setSlError("");
    setTpError("");
    setHasError(false);
  };

  const resetDefault = () => {
    setQuantity("");
    setSL("");
    setTP("");
    setEnableSL(false);
    setEnableTP(false);
    setTotal(0);
    setNet(0);
    setPriceError("");
    setSlError("");
    setTpError("");
    setHasError(false);
    setSubmitLock(false);
  };

  useEffect(() => {
    resetDefault();
  }, [orderType, side]);

  useEffect(() => {
    resetState();
  }, [selectedSymbol]);

  const SLTP_PERCENTAGE = 0.005;

  const livePrice = useSelector(
    (state) => state.market.prices[selectedSymbol?.toUpperCase()]
  );

  // console.log(livePrice, "livePrice");

  const selectedSymbolObj = watchlist.find(
    (item) => item.symbol === selectedSymbol
  );
  const instrumentId = selectedSymbolObj?.instrument_id;

  const getCurrentLivePrice = () => {
    if (!livePrice) return 0;
    return side === "BUY"
      ? parseFloat(livePrice.ask || 0)
      : parseFloat(livePrice.bid || 0);
  };

  useEffect(() => {
    if (existingOrder) {
      setSide(existingOrder.side || "BUY");
      setQuantity(existingOrder.quantity || "");
      setPrice(existingOrder.price || "");
      setOrderType(
        existingOrder.order_type === "stop-loss"
          ? "stop"
          : existingOrder.order_type
      );

      if (existingOrder.take_profit) {
        setEnableTP(true);
        setTP(existingOrder.take_profit);
      }
      if (existingOrder.stop_loss) {
        setEnableSL(true);
        setSL(existingOrder.stop_loss);
      }
    }
  }, [existingOrder]);

  useEffect(() => {
    const priceUsed =
      orderType === "market" ? getCurrentLivePrice() : parseFloat(price || 0);
    const qty = parseFloat(quantity || 0);
    const lev = parseFloat(leverage || 1);
    const symbol = selectedSymbol?.toUpperCase() || "";
    const segment = selectedSegment;

    if (!priceUsed || !qty || !segment) {
      setTotal("0.0000");
      setNet("0.0000");
      return;
    }

    let total = 0;

    if (segment === "forex") {
      const isJPY = symbol.endsWith("JPY");
      const isMetal = symbol.startsWith("XAU") || symbol.startsWith("XAG");

      if (isMetal) {
        // Metals: (100 * price * lots)
        total = (100 * priceUsed * qty) / lev;
      } else if (isJPY) {
        // JPY pairs
        total = (100000 * qty * priceUsed) / lev / priceUsed;
      } else {
        // Normal forex
        total = (100000 * priceUsed * qty) / lev;
      }
    } else {
      // Crypto & Equity
      total = priceUsed * qty;
    }

    // --- 💰 Commission Calculation ---
    let fee = 0;

    if (commissionType === "Flat") {
      fee =
        side === "BUY"
          ? parseFloat(buyCommission || 0)
          : parseFloat(sellCommission || 0);
    } else if (commissionType === "Percentage") {
      let costBasis = priceUsed * qty;

      if (segment === "forex") {
        const isJPY = symbol.endsWith("JPY");
        const isMetal = symbol.startsWith("XAU") || symbol.startsWith("XAG");

        if (isMetal) {
          costBasis = priceUsed * 100 * qty;
        } else if (isJPY) {
          costBasis = 100000 * qty; // simplified from earlier
        } else {
          costBasis = priceUsed * 100000 * qty;
        }
      }

      const commissionRate =
        side === "BUY"
          ? parseFloat(buyCommission || 0)
          : parseFloat(sellCommission || 0);

      fee = (costBasis * commissionRate) / 100;
    }

    const net = total + fee;

    setTotal(total.toFixed(4));
    setNet(net.toFixed(4));
  }, [
    price,
    quantity,
    livePrice,
    orderType,
    side,
    selectedSegment,
    selectedSymbol,
    leverage,
    buyCommission,
    sellCommission,
    commissionType,
  ]);

  useEffect(() => {
    if (!livePrice || orderType === "market") return;
    if (!priceInitializedRef.current) {
      const current = getCurrentLivePrice();
      setPrice(current);
      priceInitializedRef.current = true;
    }
  }, [orderType, livePrice, side]);

  useEffect(() => {
    if (orderType === "limit" || orderType === "stop") {
      priceInitializedRef.current = false;
    }
  }, [orderType, side]);

  const isValid = () => {
    if (!isEditing && (!quantity || parseFloat(quantity) <= 0)) return false;
    if (!isEditing && ["limit", "stop"].includes(orderType)) {
      if (!price || parseFloat(price) <= 0 || priceError) return false;
    }
    if (enableSL && (!sl || slError)) return false;
    if (enableTP && (!tp || tpError)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitLock || !isValid()) return;
    setSubmitLock(true);

    const basePayload = {
      user_id: userData?.id,
      broker_id: userData?.refred_by_id,
      instrument_id: instrumentId,
      stop_loss: enableSL ? parseFloat(sl) : null,
      take_profit: enableTP ? parseFloat(tp) : null,
    };

    const payload = {
      ...basePayload,
      side,
      order_type: orderType === "stop" ? "stop-loss" : orderType,
      price,
      quantity: parseFloat(quantity),
      leverage: 1,
    };

    const res =
      orderType === "market"
        ? await createMarketOrder(payload)
        : await createOrder(payload);

    if (res?.data?.success) {
      resetDefault();
      // toast.success("Order placed successfully", {
      //   position: "top-right",
      // });
    } else {
      toast.error(res?.error?.data?.message || "Failed to place order", {
        position: "top-right",
      });
    }
  };

  const handleModify = async (e) => {
    e.preventDefault();
    if (!isEditing) return;

    const basePayload = {
      user_id: userData?.id,
      broker_id: userData?.refred_by_id,
      instrument_id: instrumentId,
      stop_loss: enableSL ? parseFloat(sl) : null,
      take_profit: enableTP ? parseFloat(tp) : null,
    };

    if (isEditing) {
      const res = await modifyOrder({
        ...basePayload,
        order_id: existingOrder.id,
      });
      if (res?.data?.success) {
        // toast.success("Order updated successfully");
        // setIsOrderBoxShow(false);
        resetState();
      } else {
        toast.error(res?.error?.data?.message || "Failed to update order");
      }
    }
  };

  const isValidPriceWithinRange = (price, currentPrice, percentage = 0.02) => {
    const priceFloat = parseFloat(price);
    const min = currentPrice * (1 - percentage);
    const max = currentPrice * (1 + percentage);
    if (priceFloat < min || priceFloat > max) {
      return {
        valid: false,
        message: `Price must be between ${min.toFixed(6)} and ${max.toFixed(
          6
        )}`,
      };
    }
    return { valid: true, message: "" };
  };

  const getDecimalPlaces = (stepSize) => {
    if (!stepSize) return 8;
    const decimal = stepSize.toString().split(".")[1];
    return selectedSegment === "equity" ? 2 : decimal ? decimal.length : 0;
  };

  if (!isOrderBoxShow) return null;

  return (
    <div className="dash-grid-two">
      <div className="ne_box h_100 p-3 br10 bg2 border_box">
        <div className="d-flex mb-2">
          <h6 className="text-left drag-handle mr-auto">
            {isEditing ? "Modify Order" : "Trading"}
          </h6>
          <i
            onClick={() => {
              setIsOrderBoxShow(false);
              getExistingOrder(null);
              resetState();
            }}
            className="bi bi-x-lg cursor-pointer"
          />
        </div>

        {!isEditing && (
          <>
            <ul className="exchange_tab2 drag-handle br10 d-flex mb-3 position-relative">
              <li
                className={
                  side === "SELL" ? "drag-cancel my_color_sell" : "drag-cancel"
                }
                onClick={() => setSide("SELL")}
              >
                <span className="d-block fw600 mb-1">Sell</span>
                {formatDecimal(livePrice?.bid)}
              </li>
              <li
                className={
                  side === "BUY" ? "drag-cancel my_color_buy" : "drag-cancel"
                }
                onClick={() => setSide("BUY")}
              >
                <span className="d-block fw600 mb-1">Buy</span>
                {formatDecimal(livePrice?.ask)}
              </li>
            </ul>

            <div className="position_tab position_tab_center d-md-flex mb-3">
              {["market", "limit", "stop"].map((type) => (
                <a
                  key={type}
                  href="#!"
                  onClick={() => setOrderType(type)}
                  className={orderType === type ? "active" : ""}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </a>
              ))}
            </div>
          </>
        )}

        <form
          onSubmit={isEditing ? handleModify : handleSubmit}
          autoComplete="off"
        >
          {isEditing ? (
            <div className="orderforms-inputs">
              <div className="field f-cb mb-3">
                <label>Take Profit:</label>
                <input
                  type="text"
                  value={tp}
                  placeholder="Enter TP"
                  onChange={(e) => {
                    setEnableTP(true);
                    setTP(e.target.value);
                  }}
                />
              </div>

              <div className="field f-cb mb-3">
                <label>Stop Loss:</label>
                <input
                  type="text"
                  value={sl}
                  placeholder="Enter SL"
                  onChange={(e) => {
                    setEnableSL(true);
                    setSL(e.target.value);
                  }}
                />
              </div>

              <button
                className="btn w100 my_color_buy"
                type="submit"
                // disabled={!isValid()}
              >
                <div>
                  <span>Update SL/TP</span>
                  <span className="btn_small d-block">
                    {selectedSymbol?.toUpperCase()} MODIFY
                  </span>
                </div>
              </button>
            </div>
          ) : (
            <div className="orderforms-inputs">
              <div className="box-qty">
                <div className="field f-cb mb-2">
                  <label>Quantity</label>
                  <div className="iptwrap">
                    {/* <input
                    type="number"
                    step="any"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  /> */}
                    <QuantityInput
                      segment={selectedSegment}
                      stepSize={stepSize}
                      minValue={stepSize}
                      value={quantity}
                      onChange={setQuantity}
                      setHasError={setHasError}
                      tabChanging={side || orderType}
                    />
                  </div>
                </div>

                {orderType !== "market" && (
                  <div className="field f-cb mb-2">
                    <label>Price</label>
                    <div className="iptwrap">
                      {/* <input
                      type="text"
                      step="any"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    /> */}

                      {/* <input
                        type="text"
                        step="any"
                        value={price}
                        style={{
                          border: priceError ? "2px solid #e40058" : "",
                          borderRadius: "4px",
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPrice(val);
                          const { valid, message } = isValidNumericInput(val);
                          setPriceError(valid ? "" : message);
                        }}
                      /> */}
                      <input
                        type="text"
                        step="any"
                        value={price}
                        style={{
                          border: priceError ? "2px solid #e40058" : "",
                          borderRadius: "4px",
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPrice(val);

                          const { valid, message } = isValidNumericInput(val);
                          if (!valid) {
                            setPriceError(message);
                            return;
                          }

                          const maxDecimals = getDecimalPlaces(stepSize);
                          const parts = val.split(".");
                          if (parts[1] && parts[1].length > maxDecimals) {
                            setPriceError(
                              `Max ${maxDecimals} decimal places allowed.`
                            );
                            return;
                          }

                          // ✅ Also check price within ±2%
                          if (["limit", "stop"].includes(orderType)) {
                            const current = getCurrentLivePrice();
                            const rangeCheck = isValidPriceWithinRange(
                              val,
                              current
                            );
                            setPriceError(
                              rangeCheck.valid ? "" : rangeCheck.message
                            );
                          } else {
                            setPriceError("");
                          }
                        }}
                      />
                      {priceError && (
                        <div
                          style={{
                            // color: "#ffd700",
                            background: "#333",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            marginBottom: "4px",
                            fontSize: "12px",
                            position: "absolute",
                            top: "-43px",
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {priceError}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="row pt-3 mb-3 align-items-end">
                  <div className="col-5 pr-0">
                    <div
                      className="form-check mb-2"
                      onClick={() => {
                        const checked = !enableTP;
                        setEnableTP(checked);
                        if (checked && livePrice) {
                          const current = getCurrentLivePrice();
                          const val =
                            side === "BUY"
                              ? (current * (1 + SLTP_PERCENTAGE)).toFixed(6)
                              : (current * (1 - SLTP_PERCENTAGE)).toFixed(6);
                          setTP(val);
                        } else {
                          setTP("");
                        }
                      }}
                    >
                      <input
                        className="form-check-input check_box"
                        type="checkbox"
                        checked={enableTP}
                        readOnly
                      />
                      <span className="checkmark" />
                      <label className="form-check-label pl-2">
                        Take Profit
                      </label>
                    </div>
                    {/* <input
                    type="text"
                    placeholder={0}
                    disabled={!enableTP}
                    value={tp}
                    onChange={(e) => setTP(e.target.value)}
                  /> */}
                    <input
                      type="text"
                      placeholder={0}
                      disabled={!enableTP}
                      value={tp}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTP(val);
                        const { valid, message } = isValidNumericInput(val);
                        setTpError(valid ? "" : message);
                      }}
                    />
                    {tpError && (
                      <div
                        style={{
                          // color: "#ffd700",
                          background: "#333",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          marginBottom: "4px",
                          fontSize: "12px",
                          position: "absolute",
                          top: "-43px",
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        {tpError}
                      </div>
                    )}
                  </div>

                  <div className="col-2 pl-2" />

                  <div className="col-5 pl-0">
                    <div
                      className="form-check mb-2"
                      onClick={() => {
                        const checked = !enableSL;
                        setEnableSL(checked);
                        if (checked && livePrice) {
                          const current = getCurrentLivePrice();
                          const val =
                            side === "BUY"
                              ? (current * (1 - SLTP_PERCENTAGE)).toFixed(6)
                              : (current * (1 + SLTP_PERCENTAGE)).toFixed(6);
                          setSL(val);
                        } else {
                          setSL("");
                        }
                      }}
                    >
                      <input
                        className="form-check-input check_box"
                        type="checkbox"
                        checked={enableSL}
                        readOnly
                      />
                      <span className="checkmark" />
                      <label className="form-check-label pl-2">Stop Loss</label>
                    </div>
                    {/* <input
                    type="text"
                    placeholder={0}
                    disabled={!enableSL}
                    value={sl}
                    onChange={(e) => setSL(e.target.value)}
                  /> */}
                    <input
                      type="text"
                      placeholder={0}
                      disabled={!enableSL}
                      value={sl}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSL(val);
                        const { valid, message } = isValidNumericInput(val);
                        setSlError(valid ? "" : message);
                      }}
                    />
                    {slError && (
                      <div
                        style={{
                          // color: "#ffd700",
                          background: "#333",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          marginBottom: "4px",
                          fontSize: "12px",
                          position: "absolute",
                          top: "-43px",
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        {slError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="field f-cb mb-2">
                  <label>Total</label>
                  <div className="iptwrap">
                    <input type="text" value={total} disabled />
                  </div>
                </div>

                <div
                  className="d-flex justify-content-between"
                  style={{ fontSize: 10 }}
                >
                  <p>
                    Trade Fee: {buyCommission}
                    {commissionType === "Flat" ? "" : "%"}
                  </p>
                  <p>Net Amount: {net}</p>
                </div>
              </div>
              <button
                className={`btn floating-btn w100 ${
                  (submitLock || !isValid() || hasError) && "btn-disabled"
                } ${side === "BUY" ? "my_color_buy" : "my_color_sell"}`}
                type="submit"
                disabled={submitLock || !isValid() || hasError}
              >
                <div>
                  <span>{side}</span>
                  <span className="btn_small d-block">
                    {selectedSymbol?.toUpperCase()} {orderType.toUpperCase()}
                  </span>
                </div>
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TradeBox;
